import {
  useGetFieldsGeometryQuery,
  useGetTaskQuery,
  useGetTaskRoutesQuery,
} from 'app/api';
import { selectIsConsultant } from 'app/containers/Authorization/slice/selectors';
import { fieldSubmenuEnum } from 'app/containers/Field/slice/types';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { useTaskSlice } from 'app/containers/Task/slice';
import {
  selectSingleFieldTaskListState,
  selectTaskDetailsState,
  selectTaskListState,
} from 'app/containers/Task/slice/selectors';
import { detailsViewTypeEnum } from 'app/containers/Task/slice/types';
import { RouteConstants } from 'app/routes';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { MenuInfo } from 'types';
import { getTaskListFiltersSearchQuery } from 'utils/helpers';
import useLoadPolygons from 'utils/hooks/useLoadPolygons';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import { useCreateOrUpdateTaskRoutes } from './useCreateOrUpdateTaskRoutes';
import { useDeleteTask } from './useDeleteTask';
import { useGetCreateOrUpdateTaskParams } from './useGetCreateOrUpdateTaskParams';
import useSetInitialEditTaskFields from './useSetInitialEditTaskFields';
import { useSubmitCreateOrUpdateTask } from './useSubmitCreateOrUpdateTask';
import { useTaskRecipeDescriptionAsNote } from './useTaskRecipeDescriptionAsNote';

export const useCreateOrUpdateTask = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { mobileOrTablet } = useResponsiveQuery();

  const dispatch = useDispatch();
  const { actions } = useTaskSlice();
  const { actions: mapActions } = useMapSlice();

  const {
    taskId,
    fieldId,
    isCreateTaskFromFieldDetailsPage,
    isEditPage,
  } = useGetCreateOrUpdateTaskParams();

  const envId = useSelector(selectSelectedEnvId) as number;
  const seasonId = useSelector(selectSelectedSeasonId) as number;

  const { loading: isCreateLoading } = useSelector(selectTaskDetailsState);
  const { filters: taskListFilters, currentPage: taskListPage } = useSelector(
    selectTaskListState,
  );
  const {
    filters: fieldTaskListFilters,
    currentPage: fieldTaskListPage,
  } = useSelector(selectSingleFieldTaskListState);
  const isConsultant = useSelector(selectIsConsultant);

  const { isTaskDeleteLoading, onDelete } = useDeleteTask({ taskId });

  const { currentData: task, isFetching: isTaskLoading } = useGetTaskQuery(
    { envId, taskId: taskId as number },
    { skip: !taskId },
  );

  const [selectedKey, setSelectedKey] = useState(detailsViewTypeEnum.map);

  const {
    setTaskRecipeDescriptionAsNote,
    taskRecipeDescriptionAsNote,
  } = useTaskRecipeDescriptionAsNote({ task });

  useLoadPolygons();

  useSetInitialEditTaskFields({ task });

  // Load routes data
  useGetTaskRoutesQuery(
    {
      envId,
      taskId: taskId as number,
    },
    { skip: !taskId },
  );
  useGetFieldsGeometryQuery({
    envId,
    seasonId,
    companyId: null,
    companyGroupId: null,
  });

  const { taskRoutes } = useCreateOrUpdateTaskRoutes({ taskId });

  const { onSubmit } = useSubmitCreateOrUpdateTask({
    openResourcesTab: () => setSelectedKey(detailsViewTypeEnum.resources),
    isEditPage,
    initialTask: task,
    isConsultant,
    taskRoutes,
  });

  useEffect(() => {
    if (isEditPage && taskId) {
      dispatch(actions.getTaskPlannedResources({ envId, taskId }));
    } else {
      dispatch(actions.setTaskPlannedResources([]));
      dispatch(actions.setTaskDetails({ task: null, notes: '' }));
    }

    if (isCreateTaskFromFieldDetailsPage) {
      dispatch(mapActions.setSelectedPolygonsIds([Number(fieldId)]));
    }

    dispatch(actions.getAllAvailableTaskResources(envId));
  }, [
    taskId,
    fieldId,
    isEditPage,
    isCreateTaskFromFieldDetailsPage,
    actions,
    mapActions,
    dispatch,
    envId,
  ]);

  const onClickMenu = (clickedItem: MenuInfo) => {
    const key = clickedItem.key as detailsViewTypeEnum;
    setSelectedKey(key);
  };

  const onBack = () => {
    const taskListFiltersQuery = getTaskListFiltersSearchQuery({
      filters: taskListFilters,
      currentPage: taskListPage,
    });
    let redirectUrl: string | Object = generatePath(RouteConstants.task.list, {
      envId,
    }).concat(`?${taskListFiltersQuery}`);

    if (isEditPage && taskId) {
      redirectUrl = generatePath(RouteConstants.task.details, {
        envId,
        taskId,
      });
    } else if (isCreateTaskFromFieldDetailsPage) {
      const fieldTaskListFiltersQuery = getTaskListFiltersSearchQuery({
        filters: fieldTaskListFilters,
        currentPage: fieldTaskListPage,
      });

      redirectUrl = {
        pathname: generatePath(RouteConstants.field.details, {
          envId,
          fieldId: Number(fieldId),
        }),
        search: `?tab=${fieldSubmenuEnum.tasks}&${fieldTaskListFiltersQuery}`,
      };
    }

    history.push(redirectUrl);
  };

  const onSuccessRedirectTo = useMemo(() => {
    return envId && isCreateTaskFromFieldDetailsPage
      ? generatePath(RouteConstants.field.details, {
          envId,
          fieldId: Number(fieldId),
        }).concat(`?tab=${fieldSubmenuEnum.tasks}`)
      : undefined;
  }, [isCreateTaskFromFieldDetailsPage, envId, fieldId]);

  return {
    t,
    isTaskLoading,
    mobileOrTablet,
    selectedKey,
    taskRoutes,
    taskRecipeDescriptionAsNote,
    setTaskRecipeDescriptionAsNote,
    task,
    isEditPage,
    onSuccessRedirectTo,
    setSelectedKey,
    onBack,
    onDelete,
    onClickMenu,
    isConsultant,
    isCreateLoading,
    isTaskDeleteLoading,
    taskId,
    onSubmit,
  };
};
